import { Container, Divider, Grid, Typography } from '@material-ui/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import {
    BackspaceOutlined as BackspaceOutlinedIcon,
    Search as SearchIcon,
    CloudDownload as DownloadIcon
} from '@material-ui/icons'
import MUIDataTable from "mui-datatables"
import React, { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import Date from "../../../components/Form/Date"
import SelectPartNumber from '../../../components/Form/SelectPartNumber'
import SelectSupplier from '../../../components/Form/SelectSupplier'
import TextField from '../../../components/Form/TextField'
import ButtonUI from '../../../components/ui/ButtonUI'
import LoadingUI from '../../../components/ui/LoadingUI'
import PageTitle from '../../../components/ui/PageTitleUI'
import { useAuth } from '../../../hooks/auth'
import { MvmEKanbanSearch } from '../../../interfaces/Mvm'
import MvmApi from '../../../services/MvmApi'
import getDefaultGridOptions, { defaultGridTheme, defaultHeaderOptions, YupValidation } from '../../../utils/componentUtils'
import utilsDate from '../../../utils/Date'
import permissions from '../../../utils/Permissions'
import SelectStatus from '../components/SelectStatus'
import { useStyles } from '../Style'
import exportExcelFileFrom from '../../../utils/xlsxExport';
import * as Yup from 'yup'
import { useSnackbar } from 'notistack'

interface IFormInput {
    mvmNumber: string;
    suppliers: any;
    submitDate: Date | null;
    submitDateEnd: Date | null;
    entranceDate: Date | null;
    entranceDateEnd: Date | null;
    generationDateEKBalance: Date | null;
    generationDateEKBalanceEnd: Date | null;
    partNumberCode: string;
    status: number;
    sort: string;
    sortDir: string;
    selectedpartnumber?: any;
}

const EKanban: React.FC = () => {
    const classes = useStyles()
    const [mvms, setMvms] = useState<any>()
    const [loading, setLoading] = useState(false)
    const { user, hasPermission } = useAuth()
    const { t } = useTranslation("Mvm")
    const { t: tinv } = useTranslation("MvmInvoice")
    const { t: _t } = useTranslation("General")
    const { t: tval } = useTranslation("Validation");
    const history = useHistory()
    const { enqueueSnackbar } = useSnackbar();
    const [page, setPage] = useState(0)
    const [count, setCount] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [triggerSearch, setTriggerSearch] = useState(false);
    
    const defaultValues: IFormInput = {
            mvmNumber: '',
            suppliers: [],
            submitDate: null,
            submitDateEnd: null,
            entranceDate: null,
            entranceDateEnd: null,
            generationDateEKBalance: null,
            generationDateEKBalanceEnd: null,
            partNumberCode: '',
            status: 0,
            sortDir: 'desc',
            sort: 'mvmNumber',
            selectedpartnumber: null
        };
    const { handleSubmit, reset, control, getValues, setValue, clearErrors, setError } = useForm<IFormInput>({defaultValues});

    useEffect(() => {
        if (!hasPermission(permissions.MVM.EKanban))
            history.replace('NoPermission');
    }, [history, user]);

    const columns = [
        {
            name: 'id',
            display: false,
            options: {
                display: false,
            }
        },
        {
            name: 'mvmNumber',
            label: t('mvmNumber'),
            options: {
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return  `${mvms.items[tableMeta.rowIndex].mvmNumber != null ? mvms.items[tableMeta.rowIndex].mvmNumber : ""} ${mvms.items[tableMeta.rowIndex].mviNumber != null ? `(` + mvms.items[tableMeta.rowIndex].mviNumber + `)` : "" }`;
                },
                setCellProps: () => ({                       
                    align: "left",
                }),
            }
        },
        {
            name: 'invoiceKey',
            label: t('invoiceKey'),  
            options: {
                sort: false,
                setCellProps: () => ({                       
                    align: "left",
                }),
            }
        },
        {
            name: 'invoiceNumber',
            label: t('invoiceNumber'),
            options: {
                ...defaultHeaderOptions,
                sort: false,
            }
        },
        {
            name: 'invoiceDate',
            label: t('invoiceDate'),
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        },
        {
            name: 'invoiceType',
            label: t('invoiceType'),
            options: {
                sort: false,
                setCellHeaderProps: () => ({
                    align: "center",
                  }),
                setCellProps: () => ({
                    align: "center"
                }),
                customBodyRender: (value: any) => {
                    return tinv("invoiceType_" + value);
                }
            }
        },
        {
            name: 'partNumberCode',
            label: t('partNumberCode'),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'partNumberDescription',
            label: t('partNumberDescription'),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'itemQuantity',
            label: t('itemQuantity'),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'materialReturnDescription',
            label: t('processingStatus'),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: 'submitDate',
            label: t('submitDate'),
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        },
        {
            name: 'generationDateEKBalance',
            label: tinv('generationDateEKBalance'),
            options: {
                ...defaultHeaderOptions,
                sort: false,
                customBodyRender: (value: any) => {
                    return utilsDate.DateTimeShort(value);
                }
            }
        }
    ]

    useEffect(() => {
        const data = getValues();
        
        if (triggerSearch)
            onSubmit(data);
    }, [ page, rowsPerPage, triggerSearch]);

    const exportToExcel = () => {
        setLoading(true)

        const data = getValues();
        
        search(data, 100000, page)
        .then(result => {
            if (result && result.items) {
                const headerCols = columns.filter(x => x.display != false).map(x => x.label ?? "-");
                const dataExport = result.items.map((item: any) => ({
                    mvmNumber: item.mvmNumber,
                    invoiceKey: item.invoiceKey,
                    invoiceNumber: item.invoiceNumber,
                    invoiceDate: item.invoiceDate,
                    invoiceType: item.invoiceType,
                    partNumberCode: item.partNumberCode,
                    partNumberDescription: item.partNumberDescription,
                    itemQuantity: item.itemQuantity,
                    processingStatus: item.processingStatus,
                    submitDate: item.submitDate,
                    generationDateEKBalance: item.generationDateEKBalance
                }));
                console.log(headerCols, dataExport);
                exportExcelFileFrom(dataExport, headerCols, "ekanban-export");
            }
            else {
                setMvms([])
                setCount(0)
                setPage(0)
            }
        }).catch(() => {
            setMvms([]);
        }).finally(() => {
            setLoading(false);
        })
    };

    const onSubmit = async (data: any) => {
        setTriggerSearch(false);

        var validForm = await formValidation(data);
        if (validForm == false)
            return;

        setLoading(true)

        search(data, rowsPerPage, page)
        .then(result => {
            if (result && result.items) {
                setMvms(result)
                setCount(result.totalCount)
            }
            else {
                setMvms([])
                setCount(0)
                setPage(0)
            }
        }).catch(() => {
            setMvms([]);
        }).finally(() => {
            setLoading(false);
        })
    };

    const formValidation = async (data: any) => {
        
        const schema = Yup.object().shape({
            // Ao menos um dos campos abaixo devem ser preenchidos: Data de Submissão, Data de Entrada ou Data de Emissão ou Data do Saldo
            validateOneDate: Yup.string().nullable().when(['submitDate', 'entranceDate', 'generationDateEKBalance'], {
                is: (submitDate: Date, entranceDate: Date, generationDateEKBalance: Date) => (submitDate == null && entranceDate == null && generationDateEKBalance == null),
                then: (schema) => schema.required(tval('ekanbanReport.atLeastOneDate')),
                otherwise: (schema) => schema.nullable()
            }),
        });
        
        return await YupValidation(data, schema, tval, true, enqueueSnackbar, clearErrors, setError);
    }

    const search = (data: any, _rowsPerPage: number, _pageIndex: number): Promise<any> => {
        //Buscar por fornecedores do usuario Fornecedor
        if (user.isSupplier && data.suppliers?.length === 0)
            data.suppliers = user.suppliers;

        let search: MvmEKanbanSearch = {
            supplierIds: data.suppliers?.map((element: { code: any; }) => Number(element.code)).join(','),
            submitDate: data.submitDate,
            submitDateEnd: data.submitDateEnd,
            mvmNumber: data.mvmNumber,
            entranceDate:  data.entranceDate,
            entranceDateEnd:  data.entranceDateEnd,
            generationDateEKBalance: data.generationDateEKBalance,
            generationDateEKBalanceEnd: data.generationDateEKBalanceEnd,
            partNumberCode: data.selectedpartnumber?.partnumberCode,
            status: data.status?.code,
            companyId: Number(user.company.companyID),
            pageSize: _rowsPerPage,
            pageIndex: _pageIndex,
            sortDir: data.sortDir,
            sort: data.sort,
        }

        return MvmApi.searchMvmEKanban(search);
    }
    
    return (
        <Container maxWidth={false} component="main">

            <PageTitle title={t("mvmEKanban")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>

                    <form onSubmit={handleSubmit(onSubmit)} >

                        <Grid container spacing={1}>
                            <Grid item xs={6} style={{ padding: "0px", display: 'flex', alignItems: 'center' }}>
                                <Typography className={classes.root}>
                                    {t("mvmEKanbanTitleSearch")}
                                </Typography>
                            </Grid>

                            <Grid item xs={6} style={{ padding: "0px", textAlign: 'right' }}>
                                <ButtonUI onClick={() => reset(defaultValues)} startIcon={<BackspaceOutlinedIcon />}>{t("clean")}</ButtonUI>
                                <ButtonUI type="submit" color="primary" startIcon={<SearchIcon />} onClick={(e:any) => { setTriggerSearch(true); setPage(0); e.preventDefault(); }} >{t("search")}</ButtonUI>
                                <ButtonUI type="button" color="primary" startIcon={<DownloadIcon />} onClick={exportToExcel}>{_t("export")}</ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: '10px' }} />

                        <Grid container spacing={1}>
                            <Grid item xs={1}>
                                <TextField label={t("mvmNumber")} name="mvmNumber" control={control} 
                                onInput={(e) => { (e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.slice(0,9) }}
                                fullWidth/>                                
                            </Grid>
                            <Grid item xs={2}>
                                <SelectStatus 
                                    name="status" 
                                    label={t('status')} 
                                    control={control} 
                                    fullWidth  />
                            </Grid>
                            <Grid item xs={2}>
                                <SelectSupplier 
                                    name={"suppliers"}
                                    label={t("suppliers")}
                                    control={control} 
                                    multiple={true}
                                    style={{width: '100%'}} />
                            </Grid>
                            <Grid item xs={3}>
                                <SelectPartNumber 
                                label={t("partNumber")} name="selectedpartnumber" control={control} 
                                multiline={false}
                                groupItens={true}
                                companyBOMCode=""/>
                            </Grid>                            
                            <Grid item xs={2}>
                                <Date label={t("submitDate")} name="submitDate" control={control} fullWidth/>
                            </Grid>  

                            <Grid item xs={2}>
                                <Date label={t("submitDateEnd")} name="submitDateEnd" control={control} fullWidth/>                                
                            </Grid>

                            <Grid item xs={2}>
                                <Date label={t("entranceDate")} name="entranceDate" control={control} fullWidth/>
                            </Grid>
                            <Grid item xs={2}>
                                <Date label={t("entranceDateEnd")} name="entranceDateEnd" control={control} fullWidth/>
                            </Grid>

                            <Grid item xs={2}>
                                <Date label={tinv("generationDateEKBalance")} name="generationDateEKBalance" control={control} fullWidth/>
                            </Grid>                            

                            <Grid item xs={2}>
                                <Date label={tinv("generationDateEKBalanceEnd")} name="generationDateEKBalanceEnd" control={control} fullWidth/>
                            </Grid>
                        </Grid>
                    </form>
                    {
                        loading ? <LoadingUI /> :
                            <MuiThemeProvider theme={defaultGridTheme}>
                                <MUIDataTable
                                    title=""
                                    data={mvms?.items}
                                    columns={columns} 
                                    options={{
                                        ...getDefaultGridOptions(_t),
                                        serverSide: true,
                                        pagination: true,
                                        sort: true,
                                        sortOrder: { name: getValues().sort, direction: getValues().sortDir === 'asc' ? 'asc' : 'desc' },
                                        selectToolbarPlacement: "none",
                                        filter: false,
                                        search: false,
                                        print: false,
                                        viewColumns: false,
                                        selectableRows: 'none',
                                        page,
                                        count,
                                        rowsPerPage,
                                        onColumnSortChange: (changedColumn: string, direction: string) => { 
                                            setValue('sort', changedColumn);
                                            setValue('sortDir', direction);
                                            // onSubmit({...getValues(), sort: changedColumn, sortDir: direction});
                                        },
                                        onChangePage: (pageIndex) => { setTriggerSearch(true); setPage(pageIndex) },
                                        onChangeRowsPerPage: (rowsPerPage) => { setTriggerSearch(true); setPage(0); setRowsPerPage(rowsPerPage) },
                                    }}
                                />
                            </MuiThemeProvider>
                    }
                </div>
            </div>
        </Container>
    )
}
export default EKanban;